import { AccountBalance, Add, ArrowBack } from '@mui/icons-material'
import {
  useGetBankDetailsListQuery,
  useAddBankDetailsMutation,
  useDeleteBankDetailsMutation,
} from 'feature/company/redux/company.api'
import { BankDetailsRequest } from 'feature/company/types/BankDetails'
import { ModalConfirm } from 'feature/user/components/ModalConfirm/ModalConfirm'
import { MainLayout } from 'layouts'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from 'ui'
import { LoaderInCentalPosition } from 'widget'
import { Severity } from 'widget/Notifications'
import { useActions } from 'store/hooks'

import { BankDetailsCard } from './BankDetailsCard'
import { BankDetailsForm } from './BankDetailsForm'
import styles from './BankDetailsPage.module.scss'

const INITIAL_FORM: BankDetailsRequest = {
  bankName: '',
  bik: '',
  correspondentAccount: '',
  currentAccount: '',
}

export function BankDetailsPage() {
  const navigate = useNavigate()
  const { addNotification } = useActions()

  const { data, isLoading, isFetching } = useGetBankDetailsListQuery()
  const [addBankDetails, { isLoading: isAdding }] = useAddBankDetailsMutation()
  const [deleteBankDetails, { isLoading: isDeleting }] =
    useDeleteBankDetailsMutation()

  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<BankDetailsRequest>(INITIAL_FORM)
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [isDeleteCompleted, setIsDeleteCompleted] = useState(false)

  useEffect(() => {
    if (isDeleteCompleted && !isFetching) {
      setDeleteId(null)
      setIsDeleteCompleted(false)
    }
  }, [isDeleteCompleted, isFetching])

  const handleChange = (field: keyof BankDetailsRequest, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleNumericChange = (
    field: keyof BankDetailsRequest,
    value: string,
  ) => {
    setForm(prev => ({ ...prev, [field]: value.replace(/\D/g, '') }))
  }

  const handleSubmit = async () => {
    try {
      await addBankDetails(form).unwrap()
      setForm(INITIAL_FORM)
      setShowForm(false)
      addNotification({
        text: 'Банковские реквизиты добавлены',
        severity: Severity.success,
      })
    } catch (err: any) {
      addNotification({
        text: err?.data?.message || 'Ошибка добавления реквизитов',
        severity: Severity.error,
      })
    }
  }

  const handleDelete = async () => {
    if (deleteId === null) return
    try {
      await deleteBankDetails(deleteId).unwrap()
      setIsDeleteCompleted(true)
      addNotification({
        text: 'Реквизиты удалены',
        severity: Severity.success,
      })
    } catch (err: any) {
      addNotification({
        text: err?.data?.message || 'Ошибка удаления реквизитов',
        severity: Severity.error,
      })
      setDeleteId(null)
    }
  }

  const handleCancel = () => {
    setShowForm(false)
    setForm(INITIAL_FORM)
  }

  if (isLoading) {
    return (
      <MainLayout>
        <LoaderInCentalPosition />
      </MainLayout>
    )
  }

  const items = data?.items || []
  const isProcessing = isFetching || isAdding

  return (
    <MainLayout>
      <div className='container'>
        <div className={styles.bankDetails}>
          <div className={styles.bankDetails__header}>
            <Button
              variant='text'
              onClick={() => navigate('/settings')}
              startIcon={<ArrowBack />}
              data-testid='bank-details-back'
            >
              Назад
            </Button>
          </div>

          <div className={styles.bankDetails__title}>
            <AccountBalance color='primary' />
            <span className='h1'>Банковские реквизиты</span>
          </div>

          <div className={styles.bankDetails__list}>
            {items.length === 0 && !showForm && (
              <div
                className={styles.bankDetails__emptyText}
                data-testid='bank-details-empty'
              >
                Банковские реквизиты не добавлены
              </div>
            )}

            {items.map(item => (
              <BankDetailsCard key={item.id} {...item} onDelete={setDeleteId} />
            ))}
          </div>

          {isProcessing ? (
            <div className={styles.bankDetails__loader}>
              <LoaderInCentalPosition />
            </div>
          ) : showForm ? (
            <BankDetailsForm
              form={form}
              isAdding={isAdding}
              onChange={handleChange}
              onNumericChange={handleNumericChange}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
            />
          ) : (
            <Button
              variant='contained'
              color='primary'
              startIcon={<Add />}
              onClick={() => setShowForm(true)}
              data-testid='bank-details-add'
            >
              Добавить реквизиты
            </Button>
          )}
        </div>
      </div>

      <ModalConfirm
        onConfirm={handleDelete}
        cancelText='Отмена'
        actionText='Удалить'
        modalText='Вы действительно хотите удалить реквизиты?'
        isModalOpen={deleteId !== null}
        onCancel={() => setDeleteId(null)}
        isButtonLoading={isDeleting}
      />
    </MainLayout>
  )
}
